import * as React from 'react';
import { Box } from '@mui/material';
import { copyArrayWithIndexChanged } from '../../../Utils';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { Company, MembershipTypeStrings, CompanyStatusStrings, getCompanies, updateCompanyData } from '../../../types/Company';
import { EditableTable } from '../../../subs/EditableTable';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../../contexts/SnackBarContext';

export const CompaniesAdminView = () => {
    const { showSnackbar } = useSnackbar();
    const [allData, setallData] = React.useState<Company[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllCompanies = async () => {
            const allCompaniesResponse = await getCompanies(new AbortController());
            if (allCompaniesResponse.failed) {
                showUserFeedback(allCompaniesResponse.errorText);
                return;
            }
            setallData(allCompaniesResponse.result);
        }
        fetchAllCompanies();
    }, [showUserFeedback]);

    const processRowUpdate = (newRow: GridRowModel) => {
        const changedData = allData.find(row => row.id === newRow.id);
        if (!changedData) {
            showUserFeedback('Row not found', 'error');
            return newRow;
        }
        changedData.status = newRow.status as number;
        changedData.membershipType = newRow.membershipType as number;
        changedData.name = newRow.name;
        saveHandler(changedData);
        return newRow;
    };

    const saveHandler = async (changedData: Company) => {
        const updateCompanyRequest = await updateCompanyData(changedData, new AbortController());

        if (updateCompanyRequest.failed) {
            showUserFeedback(updateCompanyRequest.errorText);
            return;
        }
        const updateCompany = updateCompanyRequest.result;
        let allCompaniesLoc = copyArrayWithIndexChanged(allData, allData.findIndex(tc => tc.id === updateCompany.id), updateCompany);
        setallData(allCompaniesLoc);
    }

    const columns: GridColDef[] = [
        {
            field: 'guid',
            headerName: 'GUID',
            flex: 2
        },
        {
            field: 'wpId',
            headerName: 'WordPress',
            flex: 1,
        },
        {
            field: 'name',
            headerName: 'Name',
            flex: 3,
        },
        {
            field: 'membershipType',
            headerName: 'Membership Type',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return MembershipTypeStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return MembershipTypeStrings[value];
            },
            valueSetter: (value, row) => {
                let membershipType = 0;
                if (MembershipTypeStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < MembershipTypeStrings.length; i++) {
                        if (MembershipTypeStrings[i] === value) {
                            membershipType = i;
                            break;
                        }
                    }
                }
                return { ...row, membershipType: membershipType };
            },
            type: 'singleSelect',
            valueOptions: MembershipTypeStrings
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return CompanyStatusStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return CompanyStatusStrings[value];
            },
            valueSetter: (value, row) => {
                let status = 0;
                if (CompanyStatusStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < CompanyStatusStrings.length; i++) {
                        if (CompanyStatusStrings[i] === value) {
                            status = i;
                            break;
                        }
                    }
                }
                return { ...row, status: status };
            },
            type: 'singleSelect',
            valueOptions: CompanyStatusStrings
        },
        {
            field: 'id'
        },
    ];

    return <React.Fragment >
        <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
            <EditableTable
                columns={columns}
                rows={allData}
                setAllRows={setallData}
                processRowUpdate={processRowUpdate}
            />
        </Box>
    </React.Fragment >
}